import React from 'react';
import { CalendarClock, CheckCircle2, ChevronRight, Flag, Gauge, History, ListTodo, Plus, Receipt, Target, TrendingUp } from 'lucide-react';
import { euros, hours, Label } from '../ui';
import { dayLabel, periodState, rangeLabel, toDay } from './dates';
import { nextMilestones, pace, roadmapGaps } from './insights';
import { BudgetLine } from './Steering';

// Sous la frise quand rien n'est ouvert : le pilotage du projet (temps, achats, CA produit), les itérations en cours et
// leur rythme, les prochains jalons, ce qui reste à préparer. Chaque ligne ouvre ce dont elle parle. En large, les
// itérations côte à côte et les jalons dans une colonne à droite ; en étroit, tout s'empile.

const VERDICT = {
  on_track: { label: 'Dans les temps', tone: 'bg-emerald-50 text-emerald-700' },
  watch: { label: 'À surveiller', tone: 'bg-amber-50 text-amber-700' },
  late: { label: 'En retard', tone: 'bg-rose-50 text-rose-700' },
  unknown: { label: 'Objectifs non chiffrés', tone: 'bg-slate-100 text-slate-500' },
};

const GAP_ICON = { vision: Target, sprints: ListTodo, objectives: ListTodo, backlog: ListTodo, horizon: CalendarClock, billing: Receipt };

const Bar = ({ value, tone }) => (
  <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
    <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.round(Math.min(1, Math.max(0, value || 0)) * 100)}%` }} />
  </div>
);

const Block = ({ title, icon: Icon, children }) => (
  <div className="border-t border-slate-100 px-4 py-4 first:border-t-0 sm:px-5">
    <Label className="mb-2 flex items-center gap-1.5">{Icon && <Icon size={12} />}{title}</Label>
    {children}
  </div>
);

const Row = ({ onClick, children, className = '' }) => (
  <button type="button" onClick={onClick}
    className={`group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 -mx-2 text-left text-[13px] hover:bg-slate-50 ${className}`}>
    {children}
    <ChevronRight size={13} className="ml-auto shrink-0 text-slate-300 group-hover:text-slate-500" />
  </button>
);

const CurrentIteration = ({ iteration, color, today, onSelect, progress = null, ca = null }) => {
  const p = pace(iteration, today);
  const verdict = VERDICT[p.verdict];
  const spentHours = Number(iteration.consumed_time_hours) || 0;
  const plannedHours = Number(iteration.budget_time_hours) || 0;
  const spentEur = Number(iteration.consumed_purchase_eur) || 0;
  const plannedEur = Number(iteration.budget_purchase_eur) || 0;
  return (
    <button type="button" onClick={() => onSelect({ iterationId: iteration.id })}
      className="block h-full w-full rounded-xl border border-slate-200/80 p-3 text-left hover:border-slate-300 hover:shadow-sm">
      <div className="flex items-start gap-2">
        <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${color.dot}`} />
        <span className="line-clamp-2 min-w-0 flex-1 text-sm font-semibold leading-snug text-slate-900">{iteration.name}</span>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${verdict.tone}`}>{verdict.label}</span>
      </div>
      <p className={`mt-1 line-clamp-2 text-[13px] leading-snug ${iteration.description ? 'text-slate-600' : 'italic text-slate-400'}`}>
        {iteration.description || 'Vision à définir'}
      </p>
      <div className="mt-2.5 space-y-2">
        <div>
          <div className="mb-1 flex justify-between text-[11px] text-slate-500">
            <span>Temps écoulé</span><span className="tabular-nums">{Math.round(p.time * 100)} %{p.daysLeft != null && p.daysLeft >= 0 ? ` · encore ${p.daysLeft} j` : ''}</span>
          </div>
          <Bar value={p.time} tone="bg-slate-400" />
        </div>
        <div>
          <div className="mb-1 flex justify-between text-[11px] text-slate-500">
            <span>Objectifs réalisés</span>
            <span className="tabular-nums">{p.total ? `${Number(iteration.completed_tokens) || 0} / ${p.total} pt` : '—'}</span>
          </div>
          <Bar value={p.done} tone={color.strong} />
        </div>
        <div>
          <div className="mb-1 flex justify-between text-[11px] text-slate-500">
            <span>Réalisation estimée</span>
            <span className="tabular-nums">{progress ? `${Math.round(progress.percent)} %` : 'pas encore estimée'}</span>
          </div>
          <Bar value={progress ? progress.percent / 100 : 0} tone="bg-indigo-500" />
        </div>
      </div>
      {(plannedHours > 0 || plannedEur > 0 || Number(iteration.valeur) > 0) && (
        <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
          {Number(iteration.valeur) > 0 && <span>CA signé {euros(iteration.valeur)}</span>}
          {ca && Number(iteration.valeur) > 0 && (
            <span title="Au % de réalisation de l'itération ; une itération terminée compte en entier">CA produit {euros(ca.produced)}</span>
          )}
          {plannedHours > 0 && (
            <span className={spentHours > plannedHours ? 'font-semibold text-rose-600' : ''}>Temps {hours(spentHours)} / {hours(plannedHours)}</span>
          )}
          {plannedEur > 0 && (
            <span className={spentEur > plannedEur ? 'font-semibold text-rose-600' : ''}>Achats {euros(spentEur)} / {euros(plannedEur)}</span>
          )}
        </div>
      )}
    </button>
  );
};

const RoadmapSummary = ({
  iterations, objectives, colors, today, billability, rights, locked, onSelect, onOpenMilestone, onNewMilestone, onNewIteration, openBacklog,
  steering = null, onOpenHistory = null,
}) => {
  const current = iterations.filter((it) => periodState(toDay(it.start_date), toDay(it.end_date), today) === 'current');
  const upcoming = iterations.filter((it) => periodState(toDay(it.start_date), toDay(it.end_date), today) === 'upcoming')
    .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
  const milestones = nextMilestones(objectives, today);
  // Montré à tous (même roadmap tenue par un autre rôle : on sait quoi leur signaler).
  const gaps = roadmapGaps({ iterations, objectives, billability, canDecideBilling: Boolean(rights?.facturabilite?.can), today });
  const open = (target) => {
    if (target === 'backlog') openBacklog();
    else if (target === 'new-iteration') { if (!locked) onNewIteration(); }
    else onSelect(target);
  };
  const first = iterations.length ? Math.min(...iterations.map((it) => toDay(it.start_date) ?? Infinity)) : null;
  const last = iterations.length ? Math.max(...iterations.map((it) => toDay(it.end_date) ?? -Infinity)) : null;
  const totalCa = iterations.reduce((sum, it) => sum + (Number(it.valeur) || 0), 0);

  const project = steering?.project;

  return (
    <div className="flex h-full flex-col">
      {steering && (
        <Block title="Pilotage du projet" icon={TrendingUp}>
          {project?.iterations > 0 && (
            <div className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
              <BudgetLine label="Temps du projet" consumed={project.hours.consumed} budget={project.hours.budget} format={hours} />
              <BudgetLine label="Achats du projet" consumed={project.purchases.consumed} budget={project.purchases.budget} format={euros} />
              <BudgetLine label="CA produit" consumed={project.ca_produced_eur || 0} budget={project.ca_eur} format={euros}
                empty="pas de CA planifié" title="CA des itérations au % de réalisation saisi ; une itération terminée compte en entier" />
            </div>
          )}
          {onOpenHistory && (
            <button type="button" onClick={onOpenHistory}
              className={`flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-indigo-700 ${project?.iterations > 0 ? 'mt-3' : ''}`}>
              <History size={11} /> Historique de la roadmap et du budget
            </button>
          )}
        </Block>
      )}
      <div className={`grid lg:grid-cols-[minmax(0,1fr)_340px] ${steering ? 'border-t border-slate-100' : ''}`}>
        <Block title={current.length > 1 ? 'Itérations en cours' : 'Itération en cours'} icon={Gauge}>
          {current.length ? (
            <div className="grid gap-2 md:grid-cols-2 2xl:grid-cols-3">
              {current.map((iteration) => (
                <CurrentIteration key={iteration.id} iteration={iteration} color={colors.get(iteration.id)} today={today} onSelect={onSelect}
                  progress={steering?.progress_by_iteration?.[String(iteration.id)] || null}
                  ca={steering?.ca_by_iteration?.[String(iteration.id)] || null} />
              ))}
            </div>
          ) : upcoming[0] ? (
            <Row onClick={() => onSelect({ iterationId: upcoming[0].id })}>
              <span className={`h-2 w-2 shrink-0 rounded-full ${colors.get(upcoming[0].id).dot}`} />
              <span className="min-w-0 truncate text-slate-700">
                Rien en cours : <span className="font-medium">{upcoming[0].name}</span> commence dans {toDay(upcoming[0].start_date) - today} j
              </span>
            </Row>
          ) : (
            <div className="space-y-2">
              <p className="text-[13px] text-slate-500">Aucune itération en cours ni à venir.</p>
              {!locked && (
                <button type="button" onClick={onNewIteration} className="flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:underline">
                  <Plus size={12} /> Prévoir la prochaine itération
                </button>
              )}
            </div>
          )}
        </Block>

        <div className="border-t border-slate-100 lg:border-l lg:border-t-0">
          <Block title="Prochains jalons" icon={Flag}>
            {milestones.length ? (
              <ul>
                {milestones.map((milestone) => (
                  <li key={milestone.id}>
                    <Row onClick={() => onOpenMilestone(milestone)}>
                      <span className={`h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] ${milestone.inDays < 0 ? 'bg-rose-500' : 'bg-rose-300'}`} />
                      <span className="min-w-0 flex-1 truncate text-slate-700">{milestone.name}</span>
                      <span className={`shrink-0 text-[11px] tabular-nums ${milestone.inDays < 0 ? 'font-semibold text-rose-600' : 'text-slate-400'}`}>
                        {milestone.inDays < 0 ? `en retard de ${-milestone.inDays} j`
                          : milestone.inDays === 0 ? "aujourd'hui" : milestone.inDays <= 14 ? `dans ${milestone.inDays} j` : dayLabel(toDay(milestone.milestone_date))}
                      </span>
                    </Row>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <p className="text-[13px] text-slate-400">Aucun jalon à venir.</p>
                {!locked && (
                  <button type="button" onClick={onNewMilestone} className="flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:underline">
                    <Plus size={12} /> Jalon
                  </button>
                )}
              </div>
            )}
          </Block>

          <Block title="À préparer" icon={ListTodo}>
            {gaps.length ? (
              <ul>
                {gaps.map((gap) => {
                  const Icon = GAP_ICON[gap.kind] || ListTodo;
                  return (
                    <li key={gap.key}>
                      <Row onClick={() => open(gap.target)}>
                        <Icon size={14} className={`shrink-0 ${gap.kind === 'billing' ? 'text-violet-500' : 'text-amber-500'}`} />
                        <span className="min-w-0 flex-1 text-slate-700">{gap.text}</span>
                      </Row>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="flex items-center gap-1.5 text-[13px] text-emerald-700">
                <CheckCircle2 size={14} /> Rien à préparer : la roadmap est à jour.
              </p>
            )}
          </Block>
        </div>
      </div>

      {iterations.length > 0 && Number.isFinite(first) && Number.isFinite(last) && (
        <p className="mt-auto border-t border-slate-100 px-4 py-3 text-[11px] text-slate-400 sm:px-5">
          {iterations.length} itération{iterations.length > 1 ? 's' : ''} · {rangeLabel(first, last)}
          {totalCa > 0 ? ` · ${euros(totalCa)} prévus` : ''}
          {project?.ca_produced_eur > 0 ? ` · ${euros(project.ca_produced_eur)} produits à date` : ''}
        </p>
      )}
    </div>
  );
};

export default RoadmapSummary;
